<template>
  <div>
    <v-row
      dense
    >
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            v-model="dirtySetParams.NMRP"
            outlined
            dense
            :label="$t('training_builder.text_input.no_of_repetitions.title')"
            :placeholder="$t('label.e_g') + ' ' + 10"
            :decimal-precision="0"
            :error-messages="errors"
          />
        </validation-provider>
      </v-col>
      <v-col>
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            :value="displayedINTDST"
            outlined
            dense
            :label="$t('training_builder.text_input.interval_distance.title')"
            :placeholder="$t('label.e_g') + ' ' + 50"
            :suffix="distanceSuffix(user.organisation_settings.unit_system)"
            :decimal-precision="1"
            :error-messages="errors"
            @input="displayedINTDST = $event"
          />
        </validation-provider>
      </v-col>
      <v-col>
        <minute-second-input
          :value="dirtySetParams.INTDR"
          :label="$t('training_builder.text_input.interval_duration.title')"
          variable="INTDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col cols="6">
        <minute-second-input
          :value="dirtySetParams.RSSTDR"
          :label="$t('training_builder.text_input.rest_time_after_set')"
          variable="RSSTDR"
          @input="handleDurationInput"
        />
      </v-col>
    </v-row>

    <v-divider class="mb-6" />

    <v-row dense>
      <v-col cols="6">
        <div class="text-body-1 mb-2">
          {{ $t('training_builder.dropdown.resistance_type.title') }}
        </div>
        <validation-provider
          v-slot="{ errors }"
        >
          <v-select
            v-model="dirtySetParams.RSTTP"
            outlined
            dense
            :placeholder="$t('training_builder.dropdown.resistance_type.placeholder')"
            :error-messages="errors"
            :items="[
              { text: $t('training_builder.dropdown.resistance_type.item.none'), value: 'None' },
              { text: $t('training_builder.dropdown.resistance_type.item.parachutes'), value: 'Parachutes' },
              { text: $t('training_builder.dropdown.resistance_type.item.buckets'), value: 'Buckets' },
              { text: $t('training_builder.dropdown.resistance_type.item.resistance_socks'), value: 'Resistance socks' }
            ]"
          />
        </validation-provider>
      </v-col>
      <v-col
        v-if="dirtySetParams.RSTTP === 'Parachutes'"
        cols="6"
      >
        <validation-provider
          v-slot="{ errors }"
        >
          <number-input
            :value="displayedTTHLNG"
            outlined
            dense
            :label="$t('training_builder.text_input.tether_length.title')"
            :placeholder="$t('label.e_g') + ' ' + 5"
            :suffix="distanceSuffix(user.organisation_settings.unit_system)"
            :decimal-precision="1"
            :error-messages="errors"
            @input="displayedTTHLNG = $event"
          />
        </validation-provider>
      </v-col>
    </v-row>

    <v-divider
      v-if="exercise.drill && user.organisation_settings && user.organisation_settings.has_drill_module && teamWithDrill"
      class="mb-4"
    />

    <v-card
      v-if="exercise.drill && user.organisation_settings && user.organisation_settings.has_drill_module && teamWithDrill"
      outlined
      class="mb-4 elevation-0 grey lighten-4"
    >
      <v-row>
        <v-col>
          <v-img
            :src="exercise.drill.preview_url"
            class="ma-3"
            width="200"
            contain
          />
        </v-col>
        <v-col>
          <v-row class="flex-column mt-2">
            <v-col>
              <span class="font-weight-bold">
                {{ exercise.drill.name }}
              </span>
            </v-col>
            <v-col>
              <div
                class="caption mb-3"
                style="-webkit-line-clamp: 4; -webkit-box-orient: vertical; display: -webkit-box; overflow: hidden; width: 100%"
              >
                {{ exercise.drill.description }}
              </div>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="auto">
          <v-btn
            text
            small
            color="primary"
            class="text-none mr-2 mt-2"
            @click="editDrill = true"
          >
            <v-icon
              left
              small
            >
              far fa-pen
            </v-icon>
            Edit for this protocol
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <card-dialog
      v-if="exercise.drill"
      :key="'Edit drill' + componentKey"
      v-model="editDrill"
      :title="$t('card_dialog.edit_drill.title')"
      :scrollable="false"
      fullscreen
      persistent
    >
      <v-card-text>
        <div
          style="height: 2px"
        >
          <v-progress-linear
            v-if="submitting"
            indeterminate
            color="primary"
            :height="2"
          />
        </div>
        <HomeView
          :drill="exercise.drill"
          :sport="currentTeam.sport.name"
          :players="players.data"
          :teams="teams"
          :user="user"
          @save="handleDrillEdit"
        />
      </v-card-text>
    </card-dialog>
  </div>
</template>

<script>
import NumberInput from '@/components/helpers/NumberInput.vue'
import { cloneVariables, convertDistanceByUnitSystem, distanceSuffix } from '@/components/training-builder/helpers'
import MinuteSecondInput from '@/components/helpers/MinuteSecondInput.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { PlayerApiService } from '@/data/services'
import { DrillApiService } from '@/data/services/DrillApiService'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'

export default {
  name: 'SwimmingParamsEntry',
  components: { CardDialog, MinuteSecondInput, NumberInput, HomeView: () => import('@ultrax-ai/drill-package') },
  props: {
    exercise: {
      type: Object,
      required: true
    },
    setParams: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dirtySetParams: cloneVariables(this.setParams),
      editDrill: false,
      players: {
        data: [],
        loading: false
      },
      submitting: false,
      componentKey: 1,
      internalIntDstInMeters: null,
      internalTTHLNGInMeters: null
    }
  },
  computed: {
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    displayedINTDST: {
      get() {
        return convertDistanceByUnitSystem(this.internalIntDstInMeters, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.INTDST = convertDistanceByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    },
    displayedTTHLNG: {
      get() {
        return convertDistanceByUnitSystem(this.internalTTHLNGInMeters, this.user.organisation_settings.unit_system, true)
      },
      set(value) {
        this.dirtySetParams.TTHLNG = convertDistanceByUnitSystem(value, this.user.organisation_settings.unit_system, false)
      }
    },
    teamWithDrill() {
      return ['Football', 'Futsal', 'Handball', 'Basketball', 'Hockey', 'US Football', 'Water Polo', 'Volleyball', 'Badminton', 'Baseball', 'Lacrosse', 'Pickleball', 'Tennis', 'Field Hockey', 'Swimming'].includes(this.currentTeam?.sport?.name) || false
    }
  },
  watch: {
    dirtySetParams: {
      deep: true,
      handler: function (newValue) {
        this.$emit('update:setParams', newValue)
      }
    },
    editDrill: function (val, oldVal) {
      if (val) this.componentKey++
      else if (oldVal) {
        this.$nextTick(() => {
          this.componentKey++
        })
      }
    }
  },
  mounted() {
    this.fetchPlayers()

    this.internalIntDstInMeters = cloneDeep(this.dirtySetParams.INTDST)
    this.internalTTHLNGInMeters = cloneDeep(this.dirtySetParams.TTHLNG)
  },
  methods: {
    distanceSuffix,
    checkIfParamExists(param) {
      if (!this.exercise.tags) return false

      return this.exercise.tags.some(t => t.tag_id === 'params' && t.value === param)
    },
    handleDurationInput(min, sec, variable) {
      this.dirtySetParams[variable] = min * 60 + sec > 0 ? min * 60 + sec : null
    },
    async fetchPlayers() {
      try {
        this.players.loading = true

        const resp = await PlayerApiService.getSimpleList({})

        this.players.data = resp.data
      } catch (e) {
        this.players.data = []
      } finally {
        this.players.loading = false
      }
    },
    async handleDrillEdit(saveData) {
      try {
        this.submitting = true

        const data = {
          name: saveData.name,
          description: saveData.description,
          preview: saveData.preview,
          state: saveData.state,
          coach_id: this.user.coach.id,
          team_ids: saveData.team_ids,
          player_ids: saveData.player_ids,
          is_hidden: true,
          tags: saveData.tags
        }

        const result = await DrillApiService.create(data)

        this.exercise.drill = result.data
        this.editDrill = false

        EventBus.$emit(this.$events.DRILL_CREATED)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.drill_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
